import { useState, useEffect, useCallback, createContext, useContext } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { api } from "./api";
import { Loader2 } from "lucide-react";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // If returning from OAuth callback, keep loading=true
    // AuthCallback will handle the session exchange and call login()
    if (window.location.hash?.includes("session_id=")) {
      // Don't set loading=false — AuthCallback will handle it
      return;
    }
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback((userData) => {
    setUser(userData);
    setLoading(false);
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* silent */ }
    localStorage.removeItem("session_token");
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, setLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Keep showing loader during OAuth callback
  if (window.location.hash?.includes("session_id=") || loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (adminOnly && !user.is_admin) return <Navigate to="/dashboard" replace />;
  if (!user.onboarding_completed && location.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
  return children;
};
